/**
 * AMHS dual-rail track + circle-junction turntable meshes.
 * Layout coords: segment along +X (length = w), gauge along Z (strokeW / h).
 * Vertical (Y) = rail profile height (depth).
 *
 * Materials assume a scene.environment (see SceneEnvironment3D) so metalness
 * reads as satin aluminum instead of flat plastic or pure black.
 */
import React, { useMemo } from 'react';
import {
  getRailBarMaterial,
  getUnitBoxGeometry,
  RAIL_POOL,
} from '../../utils/threeResourcePool';
import { getCircleJunctionTurntableBarHalfInRing } from '../../utils/topology';

/**
 * Graphite dual-rail — dark charcoal metal against silver pillars.
 */
export const BLENDER_TRACK = {
  /** White layers (cleanroom side panels) */
  railWhite: '#FFFFFF',
  rail: '#FFFFFF',
  /** Black layers */
  railBlack: '#14171A',
  railWeb: '#14171A',
  glowDone: '#14171A',
  glowProg: '#00CCFF',
  /** Small warm status light. */
  glowPend: '#FFD43B',
  glowHold: '#FF4545',
  /** Hub body (cylinder core under the top plate). */
  junction: '#252B32',
  /** Flat plate on top of the turntable — dark graphite (matches center stripe). */
  junctionDisc: '#14171A',
  switch: '#252B32',
  halfGaugeFrac: 0.2,
  railWFrac: 0.16,
  railHFrac: 0.55,
  glowHFrac: 0.15,
  glowWFrac: 0.16,
} as const;

/** Shared PBR knobs for precision-machined slate rails (needs scene.environment). */
const ALUMINUM = RAIL_POOL.aluminum;
const ALUMINUM_SOFT = RAIL_POOL.aluminumSoft;

export type GlowKind = 'done' | 'prog' | 'pend' | 'hold';

const GLOW_COLOR: Record<GlowKind, string> = {
  done: BLENDER_TRACK.glowDone,
  prog: BLENDER_TRACK.glowProg,
  pend: BLENDER_TRACK.glowPend,
  hold: BLENDER_TRACK.glowHold,
};

function glowStripRenderProps(glow: GlowKind): {
  metalness: number;
  roughness: number;
  envMapIntensity: number;
  emissive?: string;
  emissiveIntensity: number;
} {
  if (glow === 'done') {
    return {
      metalness: 0.12,
      roughness: 0.58,
      envMapIntensity: 0.35,
      emissive: '#000000',
      emissiveIntensity: 0,
    };
  }
  return {
    metalness: glow === 'prog' ? 0.35 : 0.25,
    roughness: glow === 'prog' ? 0.42 : 0.55,
    envMapIntensity: 0.6,
    emissive: GLOW_COLOR[glow],
    emissiveIntensity: glow === 'prog' ? 0.35 : 1.4,
  };
}

const UNIT_BOX = getUnitBoxGeometry();

type StraightProps = {
  /** Segment length along local X. */
  length: number;
  /** Full track width (gauge span) along local Z. */
  trackWidth: number;
  /** Vertical rail profile height (Y). */
  depth: number;
  opacity?: number;
  glow?: GlowKind;
  /** When false, omit center stripe (e.g. turntable draws one shared stripe separately). */
  centerStripe?: boolean;
  /** Extra emissive boost (e.g. OHT traveling this segment). */
  glowBoost?: number;
  y?: number;
};

function RailBar({
  length,
  width,
  height,
  color,
  opacity,
  metalness,
  roughness,
  envMapIntensity = 1,
  emissive,
  emissiveIntensity = 0,
  position,
}: {
  length: number;
  width: number;
  height: number;
  color: string;
  opacity: number;
  metalness: number;
  roughness: number;
  envMapIntensity?: number;
  emissive?: string;
  emissiveIntensity?: number;
  position: [number, number, number];
}) {
  const material = getRailBarMaterial(color, {
    metalness,
    roughness,
    envMapIntensity,
    emissive,
    emissiveIntensity,
    opacity,
  });
  // Unit box + scale shares one BufferGeometry across every rail bar.
  return (
    <mesh
      position={position}
      scale={[length, height, width]}
      geometry={UNIT_BOX}
      material={material}
    />
  );
}

/** Dimensions used by DualRailStraight3D — shared with RailInstancedLayer3D. */
export function getDualRailProfile(trackWidth: number, depth: number) {
  const half = Math.max(trackWidth * BLENDER_TRACK.halfGaugeFrac, trackWidth * 0.15);
  const railW = Math.max(trackWidth * BLENDER_TRACK.railWFrac, 0.35);
  const railH = Math.max(depth * BLENDER_TRACK.railHFrac, 1.4);
  const webH = railH * 0.75;
  const glowW = Math.max(trackWidth * BLENDER_TRACK.glowWFrac, 0.32);
  const glowH = Math.max(depth * BLENDER_TRACK.glowHFrac, 0.25);
  return { half, railW, railH, webH, glowW, glowH };
}

/** Local Y lift of {@link DualRailStraight3D} inside an elevated rail group (matches SchematicElement3D). */
export const DUAL_RAIL_LIFT = 0.5;

/** Top/bottom Y of the dual-rail cross-section in the same local space as the OHT procedural model. */
export function getDualRailVerticalBounds(
  depth: number,
  railLift: number = DUAL_RAIL_LIFT
): { railH: number; topY: number; bottomY: number } {
  const { railH } = getDualRailProfile(1, depth);
  return {
    railH,
    topY: railLift + railH * 0.5,
    bottomY: railLift - railH * 0.5,
  };
}

/** Center Y of the middle black band (layer 3) — matches {@link DualRailStraight3D} layer stack. */
export function getDualRailMiddleBandCenterY(
  depth: number,
  railLift: number = DUAL_RAIL_LIFT
): number {
  const { railH } = getDualRailProfile(1, depth);
  const topY = railH * 0.5;
  const l1H = railH * 0.12;
  const layerBelowH = (railH - l1H) / 3;
  const l3CenterY = topY - l1H - layerBelowH * 1.5;
  return railLift + l3CenterY;
}

/** Straight track segment with 4 vertical side layers: 1st (top) Black, 2nd White, 3rd Black, 4th White */
export const DualRailStraight3D: React.FC<StraightProps> = ({
  length,
  trackWidth,
  depth,
  opacity = 1,
  glow = 'done',
  centerStripe = true,
  glowBoost = 0,
  y = 0,
}) => {
  const { railH, glowW, glowH } = getDualRailProfile(trackWidth, depth);
  const glowProps = glowStripRenderProps(glow);
  const trackW = Math.max(glowW, trackWidth * 0.24);

  // 4 horizontal layers: 1st (Top) Black has reduced height; below three layers (White, Black, White) share equal height
  const l1H = railH * 0.12; // 1. Top Black (reduced height)
  const layerBelowH = (railH - l1H) / 3; // 2. Upper White, 3. Middle Black, 4. Lower White (equal height)
  const l2H = layerBelowH;
  const l3H = layerBelowH;
  const l4H = layerBelowH;

  const topY = y + railH * 0.5;
  const l1CenterY = topY - l1H * 0.5;
  const l2CenterY = topY - l1H - layerBelowH * 0.5;
  const l3CenterY = topY - l1H - layerBelowH * 1.5;
  const l4CenterY = topY - l1H - layerBelowH * 2.5;

  return (
    <group>
      {/* Layer 1 (Top): Black running line (reduced height) */}
      <RailBar
        length={length}
        width={trackW}
        height={l1H}
        color={BLENDER_TRACK.railBlack}
        opacity={opacity}
        metalness={0.82}
        roughness={0.25}
        position={[0, l1CenterY, 0]}
      />

      {/* Layer 2: Upper White horizontal layer (uniform width) */}
      <RailBar
        length={length}
        width={trackW}
        height={l2H}
        color={BLENDER_TRACK.railWhite}
        opacity={opacity}
        metalness={ALUMINUM.metalness}
        roughness={ALUMINUM.roughness}
        envMapIntensity={ALUMINUM.envMapIntensity}
        position={[0, l2CenterY, 0]}
      />

      {/* Layer 3: Middle Black horizontal layer (uniform width) */}
      <RailBar
        length={length}
        width={trackW}
        height={l3H}
        color={BLENDER_TRACK.railBlack}
        opacity={opacity}
        metalness={0.82}
        roughness={0.25}
        position={[0, l3CenterY, 0]}
      />

      {/* Layer 4 (Bottom): Lower White horizontal layer (uniform width) */}
      <RailBar
        length={length}
        width={trackW}
        height={l4H}
        color={BLENDER_TRACK.railWhite}
        opacity={opacity}
        metalness={ALUMINUM.metalness}
        roughness={ALUMINUM.roughness}
        envMapIntensity={ALUMINUM.envMapIntensity}
        position={[0, l4CenterY, 0]}
      />

      {/* Active Neon / Emissive Indicator Stripe when glow !== 'done' */}
      {centerStripe && glow !== 'done' ? (
        <RailBar
          length={length}
          width={trackW * 0.8}
          height={glowH}
          color={GLOW_COLOR[glow]}
          opacity={opacity}
          metalness={glowProps.metalness}
          roughness={glowProps.roughness}
          envMapIntensity={glowProps.envMapIntensity}
          emissive={glowProps.emissive}
          emissiveIntensity={
            glow === 'prog'
              ? glowProps.emissiveIntensity + glowBoost * 0.4
              : glowProps.emissiveIntensity + glowBoost * 1.5
          }
          position={[0, y + railH * 0.5 + glowH * 0.5, 0]}
        />
      ) : null}
    </group>
  );
};

type CurveProps = {
  radius: number;
  sweepRad: number;
  trackWidth: number;
  depth: number;
  opacity?: number;
  glow?: GlowKind;
  glowBoost?: number;
  y?: number;
  /** Chord count along the arc (Blender uses 8). */
  steps?: number;
  /** Extra arc-end reach into circle junction hubs (layout units). */
  junctionReachStart?: number;
  junctionReachEnd?: number;
};

/** Dual-rail curve as chorded segments (matches create_track._rail_arc). */
export const DualRailCurve3D: React.FC<CurveProps> = ({
  radius,
  sweepRad,
  trackWidth,
  depth,
  opacity = 1,
  glow = 'prog',
  glowBoost = 0,
  y = 0,
  steps = 8,
  junctionReachStart = 0,
  junctionReachEnd = 0,
}) => {
  const segments = useMemo(() => {
    const n = Math.max(6, steps);
    const tipPad = Math.max(trackWidth * 0.75, 2);
    const reachStart = Math.max(tipPad, junctionReachStart);
    const reachEnd = Math.max(tipPad, junctionReachEnd);
    const seamOverlap = Math.max(trackWidth * 0.4, 1);
    const items: { x: number; z: number; rotY: number; len: number }[] = [];
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * sweepRad;
      const a1 = ((i + 1) / n) * sweepRad;
      const x0 = Math.cos(a0) * radius;
      const z0 = Math.sin(a0) * radius;
      const x1 = Math.cos(a1) * radius;
      const z1 = Math.sin(a1) * radius;
      const dx = x1 - x0;
      const dz = z1 - z0;
      const chord = Math.hypot(dx, dz);
      if (chord < 1e-6) continue;
      const ux = dx / chord;
      const uz = dz / chord;
      let len = chord + seamOverlap;
      let mx = (x0 + x1) / 2;
      let mz = (z0 + z1) / 2;
      if (i === 0) {
        len += reachStart;
        mx -= ux * (reachStart * 0.5);
        mz -= uz * (reachStart * 0.5);
      }
      if (i === n - 1) {
        len += reachEnd;
        mx += ux * (reachEnd * 0.5);
        mz += uz * (reachEnd * 0.5);
      }
      items.push({
        x: mx,
        z: mz,
        rotY: -Math.atan2(dz, dx),
        len,
      });
    }
    return items;
  }, [radius, sweepRad, steps, trackWidth, junctionReachStart, junctionReachEnd]);

  return (
    <group>
      {segments.map((s, i) => (
        <group key={i} position={[s.x, 0, s.z]} rotation={[0, s.rotY, 0]}>
          <DualRailStraight3D
            length={s.len}
            trackWidth={trackWidth}
            depth={depth}
            opacity={opacity}
            glow={glow}
            glowBoost={glowBoost}
            y={y}
          />
        </group>
      ))}
    </group>
  );
};

type SwitchProps = {
  size?: number;
  depth?: number;
  opacity?: number;
  /** 0 = straight, 1 = divert (Blender 42°). */
  divert?: number;
};

/** Compact Blender-style switch marker (housing + blade + lamp) — H-connectors. */
export const BlenderSwitch3D: React.FC<SwitchProps> = ({
  size = 8,
  depth = 4,
  opacity = 1,
  divert = 0,
}) => {
  const bladeDeg = divert * 42;
  return (
    <group>
      <mesh position={[0, depth * 0.2, 0]} castShadow>
        <boxGeometry args={[size * 0.55, depth * 0.35, size * 0.55]} />
        <meshStandardMaterial
          color={BLENDER_TRACK.switch}
          metalness={ALUMINUM_SOFT.metalness}
          roughness={ALUMINUM_SOFT.roughness}
          envMapIntensity={ALUMINUM_SOFT.envMapIntensity}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
      <group rotation={[0, (bladeDeg * Math.PI) / 180, 0]}>
        <mesh position={[size * 0.22, depth * 0.45, 0]} castShadow>
          <boxGeometry args={[size * 0.55, depth * 0.12, size * 0.08]} />
          <meshStandardMaterial
            color={BLENDER_TRACK.rail}
            metalness={ALUMINUM.metalness}
            roughness={ALUMINUM.roughness}
            envMapIntensity={ALUMINUM.envMapIntensity}
          />
        </mesh>
      </group>
      <mesh position={[0, depth * 0.85, 0]}>
        <sphereGeometry args={[size * 0.08, 12, 12]} />
        <meshStandardMaterial
          color={divert > 0.5 ? BLENDER_TRACK.glowPend : BLENDER_TRACK.glowDone}
          emissive={divert > 0.5 ? BLENDER_TRACK.glowPend : '#000000'}
          emissiveIntensity={divert > 0.5 ? 2.0 : 0}
        />
      </mesh>
    </group>
  );
};

/**
 * Circle-junction drive head — motor housing + flat cap + small bar showing
 * which diameter the turntable is currently connected on (local +X).
 */
export const CircleJunctionDriveHead3D: React.FC<SwitchProps> = ({
  size = 8,
  depth = 4,
  opacity = 1,
}) => {
  const s = Math.max(size, 2);
  const h = Math.max(depth, 1.5);
  const matProps = {
    metalness: ALUMINUM.metalness,
    roughness: ALUMINUM.roughness,
    envMapIntensity: ALUMINUM.envMapIntensity,
    transparent: opacity < 1,
    opacity,
  } as const;
  const softProps = {
    metalness: ALUMINUM_SOFT.metalness,
    roughness: ALUMINUM_SOFT.roughness,
    envMapIntensity: ALUMINUM_SOFT.envMapIntensity,
    transparent: opacity < 1,
    opacity,
  } as const;

  return (
    <group>
      <mesh position={[0, h * 0.08, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[s * 0.42, s * 0.48, h * 0.16, 24]} />
        <meshStandardMaterial color={BLENDER_TRACK.switch} {...softProps} />
      </mesh>
      <mesh position={[0, h * 0.32, 0]} castShadow>
        <cylinderGeometry args={[s * 0.3, s * 0.34, h * 0.38, 8]} />
        <meshStandardMaterial color={BLENDER_TRACK.rail} {...matProps} />
      </mesh>
      <mesh position={[0, h * 0.52, 0]} castShadow>
        <cylinderGeometry args={[s * 0.38, s * 0.38, h * 0.09, 24]} />
        <meshStandardMaterial color={BLENDER_TRACK.junctionDisc} {...softProps} />
      </mesh>
    </group>
  );
};

/**
 * Hub direction bar — identical mesh + material to {@link DualRailStraight3D} center stripe (`glow="done"`).
 */
export const JunctionTopDirectionLine3D: React.FC<{
  length: number;
  trackWidth: number;
  depth: number;
  railYOffset?: number;
  opacity?: number;
}> = ({ length, trackWidth, depth, railYOffset = 0, opacity = 1 }) => {
  const { railH, glowW, glowH } = getDualRailProfile(trackWidth, depth);
  const glowProps = glowStripRenderProps('done');
  return (
    <RailBar
      length={length}
      width={glowW}
      height={glowH}
      color={GLOW_COLOR.done}
      opacity={opacity}
      metalness={glowProps.metalness}
      roughness={glowProps.roughness}
      envMapIntensity={glowProps.envMapIntensity}
      emissive={glowProps.emissive}
      emissiveIntensity={0}
      position={[0, railYOffset + railH * 0.55, 0]}
    />
  );
};

/**
 * Stub inner end as a fraction of ringR.
 * 0.98 extends slightly into the ring edge so stubs meet the rotating bar seamlessly without gaps.
 */
export const CIRCLE_JUNCTION_STUB_JOIN_FRAC = 0.42;
/** Fallback bar half-length as a fraction of ringR when `barHalfExtent` is omitted. */
export const CIRCLE_JUNCTION_BAR_EXTENT_FRAC = 1.0;
/** @deprecated Use {@link CIRCLE_JUNCTION_BAR_SEAM_OVERLAP} in topology. */
export const CIRCLE_JUNCTION_BAR_TIP_OVERLAP = 0.18;

/** Hub disc — horizontal base plate (Y-up cylinder); dark cap under turntable. */
export const BlenderJunctionDisc3D: React.FC<{
  radius: number;
  depth?: number;
  opacity?: number;
  /** Vertical center of the disc (local Y). */
  y?: number;
}> = ({ radius, depth = 1.2, opacity = 1, y = 0 }) => {
  const glowProps = glowStripRenderProps('done');
  return (
    <mesh position={[0, y, 0]} receiveShadow castShadow renderOrder={2}>
      <cylinderGeometry args={[radius, radius, depth, 48]} />
      <meshStandardMaterial
        color={BLENDER_TRACK.junctionDisc}
        metalness={glowProps.metalness}
        roughness={glowProps.roughness}
        envMapIntensity={glowProps.envMapIntensity * 0.35}
        emissive="#000000"
        emissiveIntensity={0}
        transparent={opacity < 1}
        opacity={opacity}
      />
    </mesh>
  );
};

/**
 * Continuous Circular Junction Ring Track:
 * Seamless circular junction matching the cleanroom AMHS dual-rail track standard.
 *
 * - Continuous 360-degree circular ring track where all incoming track segments connect seamlessly.
 * - Exact 4-layer black/white structure (Top Black #181E26, Upper White #F1F5F9, Middle Black #181E26, Lower White #F1F5F9).
 * - Exact track width, thickness (railH), and proportions.
 * - Coplanar with all incoming tracks at height y = 0 (aligned with DUAL_RAIL_LIFT / RAIL_LIFT).
 * - Performance-friendly: built with shared UNIT_BOX geometry via DualRailStraight3D.
 */
export const CircleJunctionRing3D: React.FC<{
  ringR: number;
  depth: number;
  trackWidth: number;
  opacity?: number;
  glow?: GlowKind;
  y?: number;
  steps?: number;
}> = ({
  ringR,
  depth,
  trackWidth,
  opacity = 1,
  glow = 'done',
  y = 0,
  steps = 36,
}) => {
  const outerR = Math.max(ringR, 1);
  const barTrackW = Math.max(trackWidth, 0.8);
  const barDepth = Math.max(depth, 1);

  const segments = useMemo(() => {
    const n = Math.max(16, steps);
    const seamOverlap = Math.max(barTrackW * 0.35, 0.6);
    const items: { x: number; z: number; rotY: number; len: number }[] = [];
    const dTheta = (Math.PI * 2) / n;
    const chordLen = 2 * outerR * Math.sin(dTheta * 0.5) + seamOverlap;

    for (let i = 0; i < n; i++) {
      const midAngle = (i + 0.5) * dTheta;
      const mx = Math.cos(midAngle) * outerR;
      const mz = Math.sin(midAngle) * outerR;
      const rotY = -midAngle - Math.PI * 0.5;

      items.push({
        x: mx,
        z: mz,
        rotY,
        len: chordLen,
      });
    }
    return items;
  }, [outerR, barTrackW, steps]);

  return (
    <group name="AMHSCircularJunctionRing">
      {segments.map((s, idx) => (
        <group key={`ring-chord-${idx}`} position={[s.x, y, s.z]} rotation={[0, s.rotY, 0]}>
          <DualRailStraight3D
            length={s.len}
            trackWidth={barTrackW}
            depth={barDepth}
            opacity={opacity}
            glow={glow}
            centerStripe={false}
            y={0}
          />
        </group>
      ))}
    </group>
  );
};

/**
 * Circle-junction hub: High-precision semiconductor cleanroom AMHS rotary junction unit.
 *
 * Engineered with:
 * 1. Stationary Slewing Bearing Ring & Stator Casing with mounting lugs and rotary encoder slots
 * 2. Active Concentric Status LED Halo (Cyan when locked, Amber when actively switching/diverting)
 * 3. Precision Branch Docking Collars with optical lock sensor diodes at each track port
 * 4. Multi-tiered Rotating Rotor Platform with radial alignment grooves and rail saddle clamps
 * 5. Flush 4-layer dual-rail track bridge with precision lead-in chamfered nose caps
 */
export const CircleJunctionTurntableMesh3D: React.FC<{
  ringR: number;
  depth: number;
  trackWidth: number;
  /** Junction footprint — used to size the bridge to the active port axis (equal both sides). */
  junctionW?: number;
  junctionH?: number;
  hubAngleDeg?: number;
  opacity?: number;
  /** 0–1 switch lamp divert (mid-rotation). */
  divert?: number;
  spokeAngles?: number[];
}> = ({
  ringR,
  depth,
  trackWidth,
  junctionW,
  junctionH,
  hubAngleDeg = 0,
  opacity = 1,
  divert = 0,
  spokeAngles,
}) => {
  const outerR = Math.max(ringR, 1);
  const barTrackW = Math.max(trackWidth, 0.8);
  const barDepth = Math.max(depth, 1);

  const halfLen = useMemo(() => {
    if (junctionW != null && junctionH != null && junctionW > 0 && junctionH > 0) {
      return getCircleJunctionTurntableBarHalfInRing(junctionW, junctionH, barTrackW);
    }
    return outerR * CIRCLE_JUNCTION_BAR_EXTENT_FRAC;
  }, [junctionW, junctionH, outerR, barTrackW]);

  const barLen = halfLen * 2;

  const { railH } = getDualRailProfile(barTrackW, barDepth);

  // Resolved branch ports: use supplied spokeAngles or default 8 standard AMHS ports
  const branchAngles = useMemo(() => {
    if (spokeAngles && spokeAngles.length > 0) return spokeAngles;
    return [0, 45, 90, 135, 180, 225, 270, 315];
  }, [spokeAngles]);

  // Track bottom reference (flush underside of rails)
  const trackBottomY = -railH * 0.5;

  // Mechanical Stacking Heights
  const rotorPlateH = Math.max(0.3, barDepth * 0.07);
  const bearingH = Math.max(0.4, barDepth * 0.09);
  const statorBaseH = Math.max(0.45, barDepth * 0.10);

  // Center Y elevations
  const rotorPlateY = trackBottomY - rotorPlateH * 0.5;
  const bearingY = trackBottomY - rotorPlateH - bearingH * 0.5;
  const statorBaseY = trackBottomY - rotorPlateH - bearingH - statorBaseH * 0.5;

  // Diameters
  const rotorRadius = outerR * 0.96;
  const bearingRadius = outerR * 1.03;
  const statorRadius = outerR * 1.10;

  // Materials & Finishes — Precision Silver & Industrial Slate Gray Combination
  const silverMetalMat = {
    color: '#CBD5E1', // Cleanroom brushed silver aluminum
    metalness: 0.85,
    roughness: 0.22,
    envMapIntensity: 1.1,
    transparent: opacity < 1,
    opacity,
  };

  const slateGrayMat = {
    color: '#475569', // Clean structural slate gray
    metalness: 0.65,
    roughness: 0.35,
    envMapIntensity: 0.85,
    transparent: opacity < 1,
    opacity,
  };

  const darkGrayMat = {
    color: '#334155', // Deep graphite gray structural base
    metalness: 0.70,
    roughness: 0.38,
    envMapIntensity: 0.80,
    transparent: opacity < 1,
    opacity,
  };

  return (
    <group name="AMHSPrecisionRotaryJunction">
      {/* ──────────────── 1. STATIONARY SLEWING CHASSIS HOUSING ──────────────── */}
      {/* Main Base Housing Disc (Dark Slate Gray) */}
      <mesh position={[0, statorBaseY, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[statorRadius * 0.99, statorRadius, statorBaseH, 64]} />
        <meshStandardMaterial {...darkGrayMat} />
      </mesh>

      {/* Slewing Bearing Raceway Ring (Brushed Silver) */}
      <mesh position={[0, bearingY, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[bearingRadius, bearingRadius * 1.01, bearingH, 64]} />
        <meshStandardMaterial {...silverMetalMat} />
      </mesh>

      {/* Recessed Concentric Encoder / Guide Groove (Slate Gray) */}
      <mesh position={[0, bearingY + bearingH * 0.2, 0]}>
        <cylinderGeometry args={[bearingRadius * 0.96, bearingRadius * 0.96, bearingH * 0.35, 64]} />
        <meshStandardMaterial {...slateGrayMat} />
      </mesh>

      {/* ──────────────── 2. BRANCH DOCKING INTERFACE TRANSITIONS ──────────────── */}
      {branchAngles.map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const portR = outerR * 0.96;
        const px = Math.cos(rad) * portR;
        const pz = Math.sin(rad) * portR;
        const collarW = barTrackW * 1.16;
        const collarL = outerR * 0.20;

        return (
          <group key={`docking-port-${deg}`} position={[px, trackBottomY - 0.08, pz]} rotation={[0, -rad, 0]}>
            {/* Underside Docking Support Shoe (Dark Gray) */}
            <mesh castShadow receiveShadow>
              <boxGeometry args={[collarL, 0.16, collarW]} />
              <meshStandardMaterial {...darkGrayMat} />
            </mesh>
            {/* Beveled Side Alignment Lips (Silver) */}
            {[-1, 1].map((side) => (
              <mesh key={`lip-${side}`} position={[0, 0.08, side * (collarW * 0.48)]} castShadow>
                <boxGeometry args={[collarL, 0.08, 0.10]} />
                <meshStandardMaterial {...silverMetalMat} />
              </mesh>
            ))}
          </group>
        );
      })}

      {/* ──────────────── 3. ROTATING ROTOR PLATFORM & RAIL BRIDGE ──────────────── */}
      <group rotation={[0, -(hubAngleDeg * Math.PI) / 180, 0]}>
        {/* Upper Rotating Rotor Disc (Brushed Silver Aluminum / Cleanroom White) */}
        <mesh position={[0, rotorPlateY, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[rotorRadius, rotorRadius * 0.99, rotorPlateH, 64]} />
          <meshStandardMaterial {...silverMetalMat} />
        </mesh>

        {/* Outer Bevel Trim Ring (Slate Gray) */}
        <mesh position={[0, rotorPlateY + rotorPlateH * 0.3, 0]}>
          <cylinderGeometry args={[rotorRadius * 0.98, rotorRadius, rotorPlateH * 0.25, 64]} />
          <meshStandardMaterial {...slateGrayMat} />
        </mesh>

        {/* ──────────────── 4. DUAL-RAIL ROTATING TRACK BRIDGE ──────────────── */}
        <DualRailStraight3D
          length={barLen}
          trackWidth={barTrackW}
          depth={barDepth}
          opacity={opacity}
          glow="done"
          centerStripe={false}
          glowBoost={divert * 0.8}
          y={0}
        />
      </group>
    </group>
  );
};

